import { useState } from "react";
import { toast } from "sonner";
import { Loader2, KeyRound, Plus, Trash2, Eye, EyeOff, Upload } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useVendorLicenses, useVendorProducts, useVendorRefresh } from "@/hooks/useVendor";
import { formatDate } from "@/lib/format";
import { AdminModal, Field, Pill, inputCls, textareaCls } from "@/pages/admin/ui";

const STATUS = {
  available: { label: "Disponible", tone: "green" },
  reserved: { label: "Réservée", tone: "amber" },
  sold: { label: "Vendue", tone: "blue" },
  cancelled: { label: "Annulée", tone: "rose" },
};

export default function VendorLicenses() {
  const [filters, setFilters] = useState({ product_id: "", status: "", q: "" });
  const [reveal, setReveal] = useState(false);
  const { data, isLoading } = useVendorLicenses({ ...filters, reveal: reveal ? 1 : "" });
  const { data: products } = useVendorProducts();
  const refresh = useVendorRefresh();
  const [open, setOpen] = useState(false);
  const [importForm, setImportForm] = useState({ product_id: "", keys: "", note: "" });
  const [saving, setSaving] = useState(false);

  const items = data?.items || [];
  const stats = data?.stats || {};

  const importKeys = async () => {
    if (!importForm.product_id) { toast.error("Choisissez un produit"); return; }
    setSaving(true);
    try {
      const { data: res } = await api.post("/vendor/licenses/bulk", importForm);
      toast.success(`${res.imported} clé(s) importée(s)${res.skipped ? ` · ${res.skipped} doublon(s) ignoré(s)` : ""}`);
      setOpen(false); setImportForm({ product_id: "", keys: "", note: "" });
      refresh(["vendor-licenses", "vendor-dashboard"]);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const setStatus = async (license, status) => {
    try { await api.put(`/vendor/licenses/${license.id}`, { status }); toast.success("Statut mis à jour"); refresh(["vendor-licenses"]); }
    catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (license) => {
    if (!window.confirm("Supprimer cette clé ?")) return;
    try { await api.delete(`/vendor/licenses/${license.id}`); toast.success("Clé supprimée"); refresh(["vendor-licenses"]); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="vendor-licenses">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold text-ink">Licences & clés</h1>
          <p className="mt-1 text-sm text-slate-500">Stock de clés de vos produits numériques.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setReveal((r) => !r)} data-testid="licenses-reveal"
            className="flex items-center gap-2 rounded-full border border-line px-4 py-2.5 text-xs font-bold text-ink hover:border-brand">
            {reveal ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />} {reveal ? "Masquer" : "Révéler"} les clés
          </button>
          <button onClick={() => setOpen(true)} data-testid="licenses-import"
            className="flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-bold text-white hover:bg-brand-dark">
            <Plus className="h-4 w-4" /> Importer des clés
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {Object.entries(STATUS).map(([k, v]) => (
          <div key={k} className="rounded-2xl border border-line bg-white p-4" data-testid={`licstat-${k}`}>
            <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{v.label}</p>
            <p className="mt-1 font-display text-xl font-extrabold text-ink">{stats[k] || 0}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        <input value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
          placeholder="Rechercher une clé..." data-testid="licenses-search" className={`${inputCls} max-w-xs`} />
        <select value={filters.product_id} onChange={(e) => setFilters((f) => ({ ...f, product_id: e.target.value }))}
          data-testid="licenses-product-filter" className={`${inputCls} w-56`}>
          <option value="">Tous mes produits</option>
          {(products || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
          data-testid="licenses-status-filter" className={`${inputCls} w-44`}>
          <option value="">Tous les statuts</option>
          {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      {isLoading ? (
        <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
      ) : !items.length ? (
        <div className="rounded-2xl border border-dashed border-line bg-white p-10 text-center">
          <KeyRound className="mx-auto h-9 w-9 text-slate-300" />
          <p className="mt-3 text-sm text-slate-500">Aucune clé. Importez un stock de clés pour vos produits.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-line bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-left text-[11px] uppercase tracking-wide text-slate-400">
                <th className="p-4">Clé</th><th className="p-4">Produit</th><th className="p-4">Statut</th>
                <th className="p-4">Commande</th><th className="p-4">Ajoutée le</th><th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {items.map((l) => (
                <tr key={l.id} data-testid={`vlicense-${l.id}`}>
                  <td className="p-4 font-mono text-xs font-bold text-ink">{l.key_value}</td>
                  <td className="p-4 text-slate-600">{l.product_name}</td>
                  <td className="p-4"><Pill tone={(STATUS[l.status] || {}).tone || "slate"}>{(STATUS[l.status] || {}).label || l.status}</Pill></td>
                  <td className="p-4 text-xs text-slate-500">{l.order_number || "—"}</td>
                  <td className="p-4 text-xs text-slate-500">{formatDate(l.created_at)}</td>
                  <td className="p-4">
                    <div className="flex justify-end gap-1.5">
                      {l.status === "reserved" && (
                        <button onClick={() => setStatus(l, "available")} title="Remettre en stock"
                          className="rounded-lg border border-line px-2.5 py-1.5 text-[11px] font-bold text-slate-600 hover:border-brand hover:text-brand">
                          Restocker
                        </button>
                      )}
                      {l.status === "available" && (
                        <button onClick={() => setStatus(l, "cancelled")} title="Annuler"
                          className="rounded-lg border border-line px-2.5 py-1.5 text-[11px] font-bold text-slate-600 hover:border-amber-300 hover:text-amber-600">
                          Annuler
                        </button>
                      )}
                      {["available", "cancelled"].includes(l.status) && (
                        <button onClick={() => remove(l)} data-testid={`vlicense-del-${l.id}`}
                          className="flex h-8 w-8 items-center justify-center rounded-lg border border-line text-rose-500 hover:bg-rose-50">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AdminModal open={open} onOpenChange={setOpen} title="Importer des clés de licence">
        <div className="space-y-4">
          <Field label="Produit">
            <select value={importForm.product_id} data-testid="import-product"
              onChange={(e) => setImportForm((f) => ({ ...f, product_id: e.target.value }))} className={inputCls}>
              <option value="">— Choisir un de mes produits —</option>
              {(products || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </Field>
          <Field label="Clés (une par ligne)" hint="Les doublons déjà en stock seront ignorés automatiquement.">
            <textarea rows={6} value={importForm.keys} data-testid="import-keys"
              onChange={(e) => setImportForm((f) => ({ ...f, keys: e.target.value }))}
              placeholder={"XXXXX-XXXXX-XXXXX-XXXXX\nYYYYY-YYYYY-YYYYY-YYYYY"} className={textareaCls} />
          </Field>
          <Field label="Note (optionnel)">
            <input value={importForm.note} onChange={(e) => setImportForm((f) => ({ ...f, note: e.target.value }))} className={inputCls} placeholder="Lot fournisseur, date..." />
          </Field>
          <button onClick={importKeys} data-testid="import-submit" disabled={saving}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-brand py-3 text-sm font-bold text-white disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Importer
          </button>
        </div>
      </AdminModal>
    </div>
  );
}
